import {createAction, props} from '@ngrx/store';
import {ECheckoutStatus} from '../enums/checkout-status.enum';
import {ICustomerOrganization} from '../models/customer-organization.model';
import {Order} from '../models/order-request.model';
import {OrderResponse} from '../models/order-response.model';
import {IPatientRelation} from '../models/patient-relation.model';
import {ISampleType} from '../models/sample-type.model';

export enum CheckoutActions {
  GetOrder = '[CheckoutPage Component] get order',
  GetOrderSuccess = '[Checkout Effects] get order success',
  GetOrderFailure = '[Checkout Effects] get order failure',
  ResetGetOrderStatus = '[CheckoutPage Component] reset get order status',
  SaveOrder = '[CheckoutPage Component] saving order',
  SaveOrderSuccess = '[Checkout Effects] save order success',
  SaveOrderFailure = '[Checkout Effects] save order failure',
  UpdateOrder = '[CheckoutPage Component] update order',
  UpdateOrderFromOrderInfo = '[OrderInfo Component] update order',
  UpdateOrderFromBillingInfo = '[BillingInfo Component] update order',
  UpdateOrderFromConfirmOrder = '[OrderConfirm Component] update order',
  UpdateOrderFromPatientInfo = '[PatientInfo Component] update order',
  UpdateOrderFromClinicalInfo = '[ClinicalInfo Component] update order',
  UpdateOrderFromConsentInfo = '[ConsentInfo Component] update order',
  UpdateOrderSuccess = '[Checkout Effects] update order success',
  UpdateOrderFailure = '[Checkout Effects] update order failure',
  UpdateOrderStatus = '[CheckoutPage Component] update order status',
  UpdateOrderStatusSuccess = '[Checkout Effects] update order status success',
  UpdateOrderStatusFailure = '[Checkout Effects] update order status failure',
  GetCustomerOrganization = '[CheckoutPage Component] get customer organization',
  GetCustomerOrganizationSuccess = '[Checkout Effects] get customer organization success',
  GetCustomerOrganizationFailure = '[Checkout Effects] get customer organization failure',
  GetSelectedOrganization = '[CheckoutPage component] get selected organization',
  ResetCheckoutStatus = '[CheckoutPage component] reset checkout status',
  ConsentPdf = '[ConsentPdf Component] ConsentPdf',
  ConsentPdfSuccess = '[ConsentPdf Effects] ConsentPdf Success',
  ConsentPdfFailure = '[ConsentPdf Effects] ConsentPdf Failure',
  ConsentPdfError = '[ConsentPdf Effects] ConsentPdf Error',
  SaveTestOrder = '[NewOrder Component] saving order with test detail',
  CancelOrder = '[CancelOrder Component] CancelOrder',
  CancelOrderSuccess = '[CancelOrder Effects] CancelOrder Success',
  CancelOrderFailure = '[CancelOrder Effects] CancelOrder Failure',
  CancelOrderError = '[CancelOrder Effects] CancelOrder Error',
  GetSampleTypesFromPatientInfoPage = '[PatientInfo component] get sample types',
  GetSampleTypesFromOrderDetailsPage = '[OrderDetails component] get sample types',
  GetSampleTypesSuccess = '[Checkout Effects component] get sample types success',
  GetSampleTypesFailure = '[Checkout Effects component] get sample types failure',
  GetPatientRelationTypes = '[PatientInfo component] get patient relation types',
  GetPatientRelationTypesSuccess = '[Checkout Effects component] get patient relation types success',
  GetPatientRelationTypesFailure = '[Checkout Effects component] get patient relation types failure',
  ResetOrderFormDataFromNewOrderPage = '[new-order component] reset order form data',
  ResetOrderFormDataFromMyOrdersPage = '[my-orders component] reset order form data',
}
export const getOrder = createAction(CheckoutActions.GetOrder, props<{orderId: number}>());
export const getOrderSuccess = createAction(
  CheckoutActions.GetOrderSuccess,
  props<{orderData: OrderResponse; orderFormData: Order}>()
);
export const getOrderFailure = createAction(CheckoutActions.GetOrderFailure);
export const saveOrder = createAction(CheckoutActions.SaveOrder, props<{orderRequest: Order}>());
export const resetGetOrderStatus = createAction(CheckoutActions.ResetGetOrderStatus);
export const saveOrderSuccess = createAction(
  CheckoutActions.SaveOrderSuccess,
  props<{orderData: Order; checkoutStatus: ECheckoutStatus}>()
);
export const saveOrderFailure = createAction(
  CheckoutActions.SaveOrderFailure,
  props<{checkoutStatus: ECheckoutStatus}>()
);

export const updateOrder = createAction(CheckoutActions.UpdateOrder, props<{orderRequest: Order; isDraft: boolean}>());
export const updateOrderFromOrderInfo = createAction(
  CheckoutActions.UpdateOrderFromOrderInfo,
  props<{orderRequest: Order; isDraft: boolean}>()
);
export const updateOrderFromBillingInfo = createAction(
  CheckoutActions.UpdateOrderFromBillingInfo,
  props<{orderRequest: Order; isDraft: boolean}>()
);

export const updateOrderFromConfirmOrder = createAction(
  CheckoutActions.UpdateOrderFromConfirmOrder,
  props<{orderRequest: Order; isDraft?: boolean}>()
);
export const updateOrderFromPatientInfo = createAction(
  CheckoutActions.UpdateOrderFromPatientInfo,
  props<{orderRequest: Order; isDraft: boolean}>()
);
export const updateOrderFromClinicalInfo = createAction(
  CheckoutActions.UpdateOrderFromClinicalInfo,
  props<{orderRequest: Order; isDraft: boolean}>()
);
export const updateOrderFromConsentInfo = createAction(
  CheckoutActions.UpdateOrderFromConsentInfo,
  props<{orderRequest: Order; isDraft: boolean}>()
);

export const updateOrderSuccess = createAction(
  CheckoutActions.UpdateOrderSuccess,
  props<{
    orderData: OrderResponse;
    orderFormData: Order;
    checkoutStatus: ECheckoutStatus;
  }>()
);
export const updateOrderFailure = createAction(
  CheckoutActions.SaveOrderFailure,
  props<{checkoutStatus: ECheckoutStatus}>()
);
export const updateOrderStatus = createAction(
  CheckoutActions.UpdateOrderStatus,
  props<{orderId: number; statusId: number}>()
);
export const updateOrderStatusSuccess = createAction(
  CheckoutActions.UpdateOrderStatusSuccess,
  props<{orderData: Order}>()
);
export const updateOrderStatusFailure = createAction(CheckoutActions.UpdateOrderStatusFailure);
export const getCustomerOrganization = createAction(
  CheckoutActions.GetCustomerOrganization,
  props<{customerId: number}>()
);
export const getCustomerOrganizationSuccess = createAction(
  CheckoutActions.GetCustomerOrganizationSuccess,
  props<{customerOrganization: ICustomerOrganization[]}>()
);
export const getCustomerOrganizationFailure = createAction(CheckoutActions.GetCustomerOrganizationFailure);

export const consentPdfAction = createAction(
  CheckoutActions.ConsentPdf,
  props<{
    request: {orderId: number; productId: number; languageId: number};
  }>()
);
export const consentPdfEffectSuccess = createAction(CheckoutActions.ConsentPdfSuccess);
export const consentPdfEffectFailure = createAction(CheckoutActions.ConsentPdfFailure, props<{reason: string}>());
export const consentPdfEffectError = createAction(CheckoutActions.ConsentPdfError, props<{error: string}>());

export const cancelOrderAction = createAction(CheckoutActions.CancelOrder, props<{request: {id: number}}>());
export const cancelOrderEffectSuccess = createAction(CheckoutActions.CancelOrderSuccess, props<{response: boolean}>());
export const cancelOrderEffectFailure = createAction(CheckoutActions.CancelOrderFailure, props<{reason: string}>());
export const cancelOrderEffectError = createAction(CheckoutActions.CancelOrderError, props<{error: string}>());

export const getSelectedOrganization = createAction(
  CheckoutActions.GetSelectedOrganization,
  props<{selectedOrganization: ICustomerOrganization}>()
);

export const resetCheckoutStatus = createAction(CheckoutActions.ResetCheckoutStatus);
export const saveTestOrder = createAction(CheckoutActions.SaveTestOrder, props<{orderRequest: Order}>());

export const resetOrderFormDataFormNewOrderPage = createAction(CheckoutActions.ResetOrderFormDataFromNewOrderPage);

export const resetOrderFormDataFromMyOrdersPage = createAction(CheckoutActions.ResetOrderFormDataFromMyOrdersPage);
export const getSampleTypesFromPatientInfoPage = createAction(
  CheckoutActions.GetSampleTypesFromPatientInfoPage,
  props<{productId: number}>()
);

export const getSampleTypesFromOrderDetailsPage = createAction(CheckoutActions.GetSampleTypesFromOrderDetailsPage);

export const getSampleTypesSuccess = createAction(
  CheckoutActions.GetSampleTypesSuccess,
  props<{sampleTypes: ISampleType[]}>()
);

export const getSampleTypesFailure = createAction(CheckoutActions.GetSampleTypesFailure);

export const getPatientRelationTypes = createAction(
  CheckoutActions.GetPatientRelationTypes,
  props<{enabled: boolean}>()
);

export const getPatientRelationTypesSuccess = createAction(
  CheckoutActions.GetPatientRelationTypesSuccess,
  props<{patientRelationTypes: IPatientRelation[]}>()
);

export const getPatientRelationTypesFailure = createAction(CheckoutActions.GetPatientRelationTypesFailure);

export type UpdateOrderTypeFromSteps =
  | CheckoutActions.UpdateOrder
  | CheckoutActions.UpdateOrderFromOrderInfo
  | CheckoutActions.UpdateOrderFromBillingInfo
  | CheckoutActions.UpdateOrderFromConfirmOrder
  | CheckoutActions.UpdateOrderFromPatientInfo
  | CheckoutActions.UpdateOrderFromClinicalInfo
  | CheckoutActions.UpdateOrderFromConsentInfo;

export type updateOrderType = ReturnType<
  | typeof updateOrder
  | typeof updateOrderFromOrderInfo
  | typeof updateOrderFromBillingInfo
  | typeof updateOrderFromConfirmOrder
  | typeof updateOrderFromPatientInfo
  | typeof updateOrderFromClinicalInfo
  | typeof updateOrderFromConsentInfo
>;

export type updateOrderStatusType = ReturnType<typeof updateOrderStatus>;
export type getOrderType = ReturnType<typeof getOrder>;
export type getCustomerOrganizationType = ReturnType<typeof getCustomerOrganization>;
